import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Link } from 'react-router-dom';
import type { ResidencyRow } from '@/api/client';
import { CheckCircle2, Globe2, Info } from 'lucide-react';

// world-atlas TopoJSON via CDN (lightweight, CC0 license)
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

interface Props {
  rows: ResidencyRow[];
  globalCompliancePct: number;
}

export function DataResidencyMap({ rows, globalCompliancePct }: Props) {
  const map = new Map(rows.map((r) => [r.country, r]));

  return (
    <div className="card dashboard-panel relative h-full p-2.5 overflow-hidden">
      <div className="flex items-start justify-between gap-2">
        <div className="dashboard-title flex items-center gap-2 leading-tight">
          <Globe2 className="w-[18px] h-[18px]" /> Data Residency Map <Info className="w-3.5 h-3.5 text-[#8093aa]" />
        </div>
        <div className="rounded-md border border-[#19395a] bg-[#071d31] px-2 py-2 min-w-[104px]">
          <div className="text-[9px] text-[#dbe7f3] font-semibold">Data Residency Compliance</div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[24px] leading-none font-extrabold text-good">{globalCompliancePct}%</div>
            <CheckCircle2 className="w-[18px] h-[18px] text-good" />
          </div>
          <div className="text-[10px] text-[#90a3b8]">Global Compliance</div>
        </div>
      </div>

      <div className="relative -mt-1 h-[270px]">
        <ComposableMap
          projectionConfig={{ scale: 142 }}
          width={780}
          height={360}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo: any) => {
                const iso2 = isoNumericToAlpha2[geo.id] || '';
                const r = map.get(iso2);
                const fill = r
                  ? r.isCompliant ? '#40c96b' : '#d84449'
                  : '#48566a';
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#06172a"
                    strokeWidth={0.55}
                    style={{
                      default: { outline: 'none' },
                      hover:   { outline: 'none', fill: '#3B82F6', cursor: 'pointer' },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      <div className="mx-2 -mt-3 flex items-center justify-around rounded-md border border-[#19395a] bg-[#071d31]/95 px-2 py-3 text-[9px]">
        <Legend color="#40c96b" label={<>Compliant<br /><span>(98.6%)</span></>} />
        <Legend color="#d84449" label={<>Non-Compliant<br /><span>(1.4%)</span></>} />
        <Legend color="#48566a" label={<>No Data<br /><span>(--)</span></>} />
      </div>

      <Link
        to="/data-residency"
        className="block mt-7 -mx-2.5 border-t border-[#19395a] pt-3 text-[12px] text-[#58a6ff] text-center hover:underline"
      >
        View Data Residency Details &rarr;
      </Link>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[#b7c6d6] leading-tight">
      <span className="mt-0.5 w-3 h-3 rounded-sm" style={{ background: color }} />
      {label}
    </div>
  );
}

// world-atlas uses ISO-3166 numeric IDs; map common countries for demo usage.
const isoNumericToAlpha2: Record<string, string> = {
  '004':'AF','008':'AL','012':'DZ','032':'AR','036':'AU','040':'AT','050':'BD','056':'BE','068':'BO','076':'BR',
  '100':'BG','124':'CA','152':'CL','156':'CN','170':'CO','188':'CR','203':'CZ','208':'DK','218':'EC','222':'SV',
  '231':'ET','246':'FI','250':'FR','276':'DE','300':'GR','320':'GT','344':'HK','348':'HU','352':'IS','356':'IN',
  '360':'ID','364':'IR','368':'IQ','372':'IE','376':'IL','380':'IT','392':'JP','398':'KZ','400':'JO','404':'KE',
  '410':'KR','414':'KW','434':'LY','440':'LT','442':'LU','458':'MY','484':'MX','504':'MA','528':'NL','554':'NZ',
  '566':'NG','578':'NO','586':'PK','591':'PA','604':'PE','608':'PH','616':'PL','620':'PT','634':'QA','642':'RO',
  '643':'RU','682':'SA','702':'SG','703':'SK','705':'SI','710':'ZA','724':'ES','752':'SE','756':'CH','764':'TH',
  '784':'AE','792':'TR','804':'UA','826':'GB','840':'US','858':'UY','862':'VE','704':'VN',
};
